import { useDisclosure, useTimeout } from '@mantine/hooks'
import { KeyboardReturn } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { isEmpty } from 'lodash'
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react'
import { PopupModal } from 'react-calendly'
import { toast } from 'react-toastify'

import { ENTER_KEY } from '~/constants'
import {
  findCustomerEmail,
  formatMessages,
} from '~/features/chatbot/components/dialog/dialogInput/utils'
import {
  CALENDLY_PORTAL_ID,
  CHAT_TIMEOUT_MESSAGE,
  END_CHAT_TIMEOUT,
} from '~/features/chatbot/constants'
import useChat from '~/features/chatbot/hooks/useChat'
import useEndChatbotConversation from '~/features/chatbot/hooks/useEndChatbotConversation'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { ESiteImagesTypes } from '~/features/partners/models'
import { getImgUrlByType } from '~/features/partners/utils'
import { CALENDLY_REGEX } from '~/features/settings/constants'
import { EChatbotTheme } from '~/features/settings/models'
import useIdentifyViewportWidth from '~/features/ui/hooks/useIdentifyViewportWidth'
import EElement from '~/features/ui/models'
import getLayoutColors from '~/layouts/chatbot/utils'
import { store } from '~/store'

const DialogInput = forwardRef(
  (_, dialogInputWrapperRef: ForwardedRef<HTMLDivElement>) => {
    const [hasConversationEnded, setHasConversationEnded] = useState(false)
    const { isExtraSmallScreen, isSmallScreen } = useIdentifyViewportWidth()
    const [opened, { open, close }] = useDisclosure(false)
    const [
      chatbotLayout,
      conversationId,
      showBookAMeeting,
      messages,
      chatbotAgent,
    ] = store((state) => [
      state.chatbot.layout,
      state.chatbot.conversationId,
      state.chatbot.showBookAMeeting,
      state.chatbot.messages,
      state.chatbot.agent,
    ])
    const { partner } = usePartnerCentralized()
    const theme = useTheme()
    const { containerBackgroundColor } = getLayoutColors(chatbotLayout, theme)
    const toastId = useRef<number | string | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const conversationIdRef = useRef(conversationId)
    const agentIdRef = useRef(chatbotAgent.id)
    const iconSrc = getImgUrlByType(ESiteImagesTypes.buttonIcon, partner)
    const { mutate: chat, isPending: isPendingChat } = useChat()
    const { mutate: endConversation } = useEndChatbotConversation()
    const {
      start: startEndConversationTimeout,
      clear: clearEndConversationTimeout,
    } = useTimeout(async () => {
      if (conversationIdRef.current && agentIdRef.current) {
        endConversation({
          conversationId: conversationIdRef.current,
          agentId: agentIdRef.current,
        })
      }
      if (toastId.current) {
        toast.dismiss(toastId.current)
      }
      setHasConversationEnded(true)
    }, END_CHAT_TIMEOUT)
    const { start: startTimeoutMessage, clear: clearTimeoutMessage } =
      useTimeout(() => {
        startEndConversationTimeout()
        toastId.current = toast.warn(
          'This chat will end in 30 seconds due to inactivity. Please send another message if you would like to continue.',
          {
            autoClose: END_CHAT_TIMEOUT,
          },
        )
      }, CHAT_TIMEOUT_MESSAGE)

    const timeoutReset = () => {
      if (toastId.current) {
        toast.dismiss(toastId.current)
      }
      clearEndConversationTimeout()
      clearTimeoutMessage()
      startTimeoutMessage()
    }

    // Focus after chat is pending but NOT on mobile
    useEffect(() => {
      if (
        !isPendingChat &&
        inputRef.current &&
        !(isExtraSmallScreen || isSmallScreen)
      ) {
        inputRef.current.focus()
      }
    }, [isPendingChat, isExtraSmallScreen, isSmallScreen])

    // Keep track of conversation id and agent id as a ref since useTimeout will not re-render function passed as callback
    useEffect(() => {
      if (conversationId) {
        conversationIdRef.current = conversationId
      }
      if (chatbotAgent.id) {
        agentIdRef.current = chatbotAgent.id
      }
    }, [conversationId, chatbotAgent.id])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLInputElement

      if (event.key === ENTER_KEY) {
        event.preventDefault()
      }
      if (
        (event.code === ENTER_KEY || event.key === ENTER_KEY) &&
        !isEmpty(target.value)
      ) {
        timeoutReset()
        if (chatbotAgent.id) {
          chat({
            agentId: chatbotAgent.id,
            message: target.value,
            ...(conversationId && { conversationId }),
          })
        }

        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }
    }

    const handleAdornmentClick = () => {
      if (inputRef.current && !isEmpty(inputRef.current.value)) {
        timeoutReset()
        if (chatbotAgent.id) {
          chat({
            agentId: chatbotAgent.id,
            message: inputRef.current.value,
            ...(conversationId && { conversationId }),
          })
        }

        inputRef.current.value = ''
        inputRef.current.focus()
      }
    }

    return (
      <>
        <Box
          ref={dialogInputWrapperRef}
          id={EElement.chatbotMessageTextfield}
          sx={{
            bottom: chatbotAgent.showBranding ? 52 : 0,
            width: '100%',
            backgroundColor: {
              xxxs:
                chatbotLayout.selection.theme === EChatbotTheme.linkView
                  ? containerBackgroundColor
                  : theme.palette.common.white,
              md: alpha(theme.palette.common.white, 0),
            },
            zIndex: 1,
            ...(isSmallScreen || isExtraSmallScreen
              ? { position: 'fixed' }
              : {}),
          }}
        >
          <Stack
            sx={{
              paddingInline: 2,
              paddingBottom: 1,
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Stack gap={1}>
                <Box
                  sx={{
                    backgroundImage: `url(${iconSrc})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    width: { xxxs: 32, md: 64 },
                    height: { xxxs: 32, md: 64 },
                    display: chatbotAgent.showLibby ? 'block' : 'none',
                  }}
                />
                {!isEmpty(chatbotAgent.calendlyLink) && showBookAMeeting && (
                  <Button
                    onClick={() =>
                      CALENDLY_REGEX.test(chatbotAgent.calendlyLink ?? '')
                        ? open()
                        : window.open(chatbotAgent.calendlyLink, '_blank')
                    }
                    variant="secondary"
                  >
                    Book a Meeting
                  </Button>
                )}
              </Stack>
              <Stack
                sx={{
                  flexGrow: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: hasConversationEnded ? 'flex' : 'none',
                  color:
                    chatbotLayout.selection.theme === EChatbotTheme.linkView
                      ? theme.palette.tertiary5
                      : theme.palette.tertiary4,
                }}
              >
                <Typography variant="caption">
                  Conversation ended due to inactivity.
                </Typography>
                <Typography variant="caption">
                  Refresh the page to start a new conversation.
                </Typography>
              </Stack>
            </Box>

            <TextField
              maxRows={2}
              multiline
              inputRef={inputRef}
              disabled={hasConversationEnded}
              placeholder="Type your response..."
              hiddenLabel
              variant="standard"
              onKeyDown={handleKeyDown}
              InputProps={{
                disableUnderline: true,
                endAdornment: (
                  <IconButton
                    onClick={handleAdornmentClick}
                    disabled={isPendingChat || hasConversationEnded}
                  >
                    <KeyboardReturn />
                  </IconButton>
                ),
              }}
              sx={{
                padding: { xxxs: 1, md: 2 },
                borderRadius: 2,
                flexGrow: 1,
                backgroundColor:
                  chatbotLayout.selection.theme === EChatbotTheme.linkView
                    ? theme.palette.common.white
                    : theme.palette.tertiary5,
                color:
                  chatbotLayout.selection.theme === EChatbotTheme.linkView
                    ? chatbotLayout.options[EChatbotTheme.linkView][
                        chatbotLayout.selection[EChatbotTheme.linkView]
                      ].secondaryTextColor
                    : theme.palette.mainFont,
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.tertiary4,
                },
              }}
            />
          </Stack>
        </Box>
        <PopupModal
          open={opened}
          onModalClose={close}
          url={`${chatbotAgent.calendlyLink}?email=${encodeURIComponent(findCustomerEmail(messages) ?? '')}&a1=${encodeURIComponent(formatMessages(messages, chatbotAgent.agentName))}`}
          rootElement={
            document.getElementById(CALENDLY_PORTAL_ID) as HTMLElement
          }
        />
      </>
    )
  },
)

export default DialogInput
